import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './AuthContext';
import { getPlanByProductId, Plan, plans } from '@/lib/subscription-plans';

interface SubscriptionState {
  subscribed: boolean;
  betaTester: boolean;
  trial: boolean;
  trialPlanId: string | null;
  productId: string | null;
  priceId: string | null;
  subscriptionEnd: string | null;
  currentPlan: Plan | null;
  loading: boolean;
}

interface SubscriptionContextType extends SubscriptionState {
  checkSubscription: () => Promise<void>;
  createCheckout: (priceId: string) => Promise<string | null>;
  openCustomerPortal: () => Promise<string | null>;
  startTrial: (planId: string) => Promise<boolean>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export function SubscriptionProvider({ children }: { children: ReactNode }) {
  const { user, session } = useAuth();
  const [state, setState] = useState<SubscriptionState>({
    subscribed: false,
    betaTester: false,
    trial: false,
    trialPlanId: null,
    productId: null,
    priceId: null,
    subscriptionEnd: null,
    currentPlan: null,
    loading: true,
  });

  const checkSubscription = useCallback(async () => {
    if (!session) {
      setState(prev => ({ ...prev, subscribed: false, betaTester: false, currentPlan: null, loading: false }));
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke('check-subscription');
      
      if (error) {
        console.error('Error checking subscription:', error);
        setState(prev => ({ ...prev, loading: false }));
        return;
      }

      const isBeta = data.beta_tester === true;
      const isTrial = data.trial === true;
      const betaPlanId = data.beta_plan_id || 'premium';
      const trialPlanId = data.trial_plan_id || null;
      
      // Determine plan: beta > trial > stripe
      let plan: Plan | null = null;
      if (isBeta) {
        plan = plans.find(p => p.id === betaPlanId) || plans.find(p => p.id === 'premium') || null;
      } else if (isTrial && trialPlanId) {
        plan = plans.find(p => p.id === trialPlanId) || null;
      } else if (data.product_id) {
        plan = getPlanByProductId(data.product_id) || null;
      }

      setState({
        subscribed: data.subscribed || false,
        betaTester: isBeta,
        trial: isTrial,
        trialPlanId: trialPlanId,
        productId: data.product_id || null,
        priceId: data.price_id || null,
        subscriptionEnd: data.subscription_end || null,
        currentPlan: plan,
        loading: false,
      });
    } catch (error) {
      console.error('Error checking subscription:', error);
      setState(prev => ({ ...prev, loading: false }));
    }
  }, [session]);

  const createCheckout = async (priceId: string): Promise<string | null> => {
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { priceId },
      });

      if (error) {
        console.error('Error creating checkout:', error);
        return null;
      }

      return data.url;
    } catch (error) {
      console.error('Error creating checkout:', error);
      return null;
    }
  };

  const openCustomerPortal = async (): Promise<string | null> => {
    try {
      const { data, error } = await supabase.functions.invoke('customer-portal');

      if (error) {
        console.error('Error opening customer portal:', error);
        return null;
      }

      return data.url;
    } catch (error) {
      console.error('Error opening customer portal:', error);
      return null;
    }
  };

  const startTrial = async (planId: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase.functions.invoke('start-trial', {
        body: { planId },
      });

      if (error) {
        console.error('Error starting trial:', error);
        return false;
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      // Refresh subscription state
      await checkSubscription();
      return true;
    } catch (error) {
      console.error('Error starting trial:', error);
      throw error;
    }
  };

  useEffect(() => {
    checkSubscription();
  }, [checkSubscription]);

  // Auto-refresh subscription status every minute
  useEffect(() => {
    if (!session) return;
    
    const interval = setInterval(checkSubscription, 60000);
    return () => clearInterval(interval);
  }, [session, checkSubscription]);

  return (
    <SubscriptionContext.Provider
      value={{
        ...state,
        checkSubscription,
        createCheckout,
        openCustomerPortal,
        startTrial,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
}
